import { PageTitle as SharedPageTitle } from '../../../components/layout/PageHeader';
import React from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import { useUserFiles } from '../../../hooks/useApi';
import { useDebounce } from '../../../hooks/useDebounce';
import type { UserFileItem, UserFileKind } from '../../../types/api';
import { getUserFileDownloadUrl } from '../lib/downloadUserFile';
import { UserFilesFilters } from './UserFilesFilters';
import { UserFilesPagination } from './UserFilesPagination';
import { UserFilesTable } from './UserFilesTable';

const PER_PAGE = 20;

const Container = styled.div<{ $embedded: boolean }>`
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
`;

const Header = styled.header`
  margin-bottom: var(--space-xl);
`;

const Title = styled(SharedPageTitle)``;

const Subtitle = styled.p`
  margin: 0.5rem 0 0;
  max-width: 65ch;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-muted);
`;

const StateCard = styled(Card)`
  padding: var(--space-3xl);
  text-align: center;
`;

const StateMessage = styled.p`
  margin: 0 0 var(--space-md);
  color: var(--text-secondary);
`;

const Updating = styled.p`
  margin: 0 0 var(--space-sm);
  color: var(--text-muted);
  font-size: var(--font-size-sm);
`;

interface UserFilesViewProps {
  embedded?: boolean;
}

export const UserFilesView: React.FC<UserFilesViewProps> = ({
  embedded = false,
}) => {
  const [search, setSearch] = React.useState('');
  const [kind, setKind] = React.useState<UserFileKind | ''>('');
  const [page, setPage] = React.useState(1);
  const [downloadingId, setDownloadingId] = React.useState<string | null>(null);
  const debouncedSearch = useDebounce(search.trim(), 300);

  const params = React.useMemo(
    () => ({
      page,
      per_page: PER_PAGE,
      ...(kind ? { kind } : {}),
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    }),
    [debouncedSearch, kind, page],
  );
  const { data, isLoading, isFetching, error, refetch } = useUserFiles(params);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasFilters = Boolean(search || kind);

  const handleDownload = React.useCallback(async (file: UserFileItem) => {
    setDownloadingId(file.id);
    try {
      const url = await getUserFileDownloadUrl(file);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (downloadError) {
      toast.error(
        downloadError instanceof Error
          ? downloadError.message
          : 'No se pudo descargar el archivo.',
      );
    } finally {
      setDownloadingId(null);
    }
  }, []);

  const handleSearchChange = React.useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleKindChange = React.useCallback((value: UserFileKind | '') => {
    setKind(value);
    setPage(1);
  }, []);

  const handleClear = React.useCallback(() => {
    setSearch('');
    setKind('');
    setPage(1);
  }, []);

  return (
    <Container $embedded={embedded}>
      {!embedded && (
        <Header>
          <Title>Mis archivos</Title>
          <Subtitle>
            Consulta y descarga los documentos asociados a tu cuenta.
          </Subtitle>
        </Header>
      )}

      <UserFilesFilters
        search={search}
        kind={kind}
        onSearchChange={handleSearchChange}
        onKindChange={handleKindChange}
        onClear={handleClear}
      />

      {isFetching && !isLoading && (
        <Updating role="status" aria-live="polite">
          Actualizando archivos…
        </Updating>
      )}

      {isLoading ? (
        <StateCard>
          <LoadingSpinner message="Cargando archivos..." />
        </StateCard>
      ) : error ? (
        <StateCard role="alert">
          <StateMessage>No se pudieron cargar tus archivos.</StateMessage>
          <Button variant="primary" size="sm" onClick={() => refetch()}>
            Reintentar
          </Button>
        </StateCard>
      ) : items.length === 0 ? (
        <StateCard>
          <StateMessage>
            {hasFilters
              ? 'No se encontraron archivos con los filtros aplicados.'
              : 'Aún no tienes archivos disponibles.'}
          </StateMessage>
          {hasFilters && (
            <Button variant="tertiary" size="sm" onClick={handleClear}>
              Limpiar filtros
            </Button>
          )}
        </StateCard>
      ) : (
        <>
          <UserFilesTable
            items={items}
            downloadingId={downloadingId}
            onDownload={handleDownload}
          />
          <UserFilesPagination
            page={data?.page ?? page}
            perPage={data?.per_page ?? PER_PAGE}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}
    </Container>
  );
};
